import type { ChangeEvent } from 'react';
import type { UseFormReturnType } from '@mantine/form';

import type { KYCFormValues } from '../types';

// Composite DOB errors land on these fields: invalid-date on day, age-restriction on year.
const DOB_ERROR_FIELDS = ['dob.day', 'dob.year'];

/**
 * Shared helper for KYC forms that need the DOB cross-field re-validation workaround.
 * When any DOB sub-field changes, we re-trigger validation of the fields that hold
 * the composite superRefine errors (invalid-date, age-restriction) so they stay in sync.
 */
export function useKYCFormHelpers(form: UseFormReturnType<KYCFormValues>): {
  formFieldChange: (field: string, e: ChangeEvent<HTMLInputElement>) => void;
  stateFieldChange: (e: ChangeEvent<HTMLInputElement>) => void;
} {
  // Apply a field's own change, then re-trigger validation of the dependent fields
  // whose composite superRefine errors must stay in sync (DOB → day/year, state → zip).
  function changeAndRevalidate(
    changedField: string,
    e: ChangeEvent<HTMLInputElement>,
    dependentFields: string[],
  ): void {
    form.getInputProps(changedField).onChange(e);

    dependentFields
      .filter((dependentField) => dependentField !== changedField)
      .forEach((dependentField) => {
        const dependent = form.getInputProps(dependentField);
        if (dependent.value.length > 0) {
          dependent.onChange(dependent.value);
        }
      });
  }

  function formFieldChange(field: string, e: ChangeEvent<HTMLInputElement>): void {
    changeAndRevalidate(field, e, DOB_ERROR_FIELDS);
  }

  function stateFieldChange(e: ChangeEvent<HTMLInputElement>): void {
    changeAndRevalidate('state', e, ['zip']);
  }

  return { formFieldChange, stateFieldChange };
}
